import { UcoastEl } from '@/scripts/core/UcoastEl'
import { mediaLoader } from '@/scripts/core/global'
import { SliderComponent } from '@/scripts/theme/slider-component'
import { type QuickAddModal } from '@/scripts/optional/quick-add'

export class ResaCollectionTabs extends UcoastEl {
	static htmlSelector = 'resa-collection-tabs'
	tabs: HTMLAnchorElement[] = []
	panels: HTMLElement[] = []
	private frame?: number
	private modals: QuickAddModal[] = []
	private onClick = (event: Event) => {
		const tab = (event.target as Element).closest<HTMLAnchorElement>('[data-collection-tab]')
		if (!tab || !this.tabs.includes(tab)) return
		event.preventDefault()
		this.select(this.tabs.indexOf(tab))
	}
	private onKeyDown = (event: KeyboardEvent) => {
		const index = this.tabs.indexOf(event.target as HTMLAnchorElement)
		if (index < 0) return
		let next: number
		switch (event.key) {
			case 'ArrowLeft': next = (index + this.tabs.length - 1) % this.tabs.length; break
			case 'ArrowRight': next = (index + 1) % this.tabs.length; break
			case 'Home': next = 0; break
			case 'End': next = this.tabs.length - 1; break
			case ' ': case 'Enter': next = index; break
			default: return
		}
		event.preventDefault()
		this.select(next)
		this.tabs[next].focus()
	}
	private onBlockSelect = (event: Event) => {
		const blockId = (event as CustomEvent<{ blockId: string }>).detail?.blockId
		const index = this.panels.findIndex(panel => panel.dataset.blockId === blockId)
		if (index >= 0) this.select(index)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.tabs = Array.from(this.querySelectorAll<HTMLAnchorElement>('[data-collection-tab]'))
		this.panels = Array.from(this.querySelectorAll<HTMLElement>('[data-collection-panel]'))
		// Quick-add moves its modals outside the section; retain ownership for editor reloads.
		this.modals = Array.from(document.querySelectorAll<QuickAddModal>('quick-add-modal[data-resa-collection-panel]'))
			.filter(modal => this.panels.some(panel => panel.id === `CollectionPanel-${modal.dataset.resaCollectionPanel}`))
		this.querySelector('[data-tab-list]')?.setAttribute('role', 'tablist')
		this.tabs.forEach((tab, index) => {
			tab.setAttribute('role', 'tab')
			tab.setAttribute('aria-controls', this.panels[index].id)
			this.panels[index].setAttribute('role', 'tabpanel')
		})
		this.addEventListener('click', this.onClick)
		this.addEventListener('keydown', this.onKeyDown)
		document.addEventListener('shopify:block:select', this.onBlockSelect)
		this.select(Math.max(0, this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true')))
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.onClick)
		this.removeEventListener('keydown', this.onKeyDown)
		document.removeEventListener('shopify:block:select', this.onBlockSelect)
		if (this.frame !== undefined) cancelAnimationFrame(this.frame)
		this.modals.forEach(modal => {
			if (modal.parentElement !== document.body) return
			if (modal.hasAttribute('open') && typeof modal.hide === 'function') modal.hide(true)
			modal.remove()
		})
		this.modals = []
	}

	select(index: number) {
		if (!this.panels[index]) return
		this.tabs.forEach((tab, i) => {
			tab.setAttribute('aria-selected', String(i === index))
			tab.tabIndex = i === index ? 0 : -1
			this.panels[i].hidden = i !== index
		})
		if (this.frame !== undefined) cancelAnimationFrame(this.frame)
		this.frame = requestAnimationFrame(() => {
			this.panels[index].querySelectorAll<SliderComponent>('slider-component').forEach(slider => {
				if (slider instanceof SliderComponent) slider.resetPages()
			})
			mediaLoader(true)
		})
	}
}
